import Icon from '@components/Icon'
import { INVOICE_LIST } from '@constants/internal-route.constant'
import { useBodyClasses } from '@layout/useBodyClasses'
import { useColorMode } from '@layout/console/useColorMode'
import { BillingService } from '@services/api/billing.service'
import { initPaddle, openPaddleCheckout } from '@services/utils/paddle.service'
import { ENV } from 'config/ENV.config'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Paddle's "Default payment link" target — Checkout > Checkout settings in the Paddle dashboard.
 *
 * Paddle appends `?_ptxn=<transaction id>` to that URL for every link IT originates: abandoned
 * checkout recovery, manually-collected invoices, and its own emails. This page exists because
 * nothing else can serve those links:
 *
 *   * the invoice list keys off OUR invoice id, not Paddle's transaction id;
 *   * it is behind the login wall, and bouncing someone arriving from an email to a login form is
 *     what makes a recovery link useless;
 *   * the webhook URL answers JSON to a browser, and a plain home page has no Paddle.js.
 *
 * So it is deliberately PUBLIC and session-free. That is safe because it reads no session and
 * mutates nothing: the transaction id is Paddle's, the amount lives on Paddle's transaction, and
 * the payment is still confirmed solely by the HMAC-verified webhook. The worst a stranger with a
 * guessed id achieves is opening a checkout they would have to pay themselves.
 */

/** Paddle transaction ids look like `txn_01hxyz…`. Anything else never reaches Paddle.js. */
const TXN_PATTERN = /^txn_[A-Za-z0-9]+$/

type Phase = 'opening' | 'retry' | 'done' | 'error'

const PaddleCheckout = () => {
	const [searchParams] = useSearchParams()
	const [phase, setPhase] = useState<Phase>('opening')
	const [message, setMessage] = useState('')
	const [ready, setReady] = useState(false)
	useColorMode()
	useBodyClasses('bg-body-secondary')

	// Paddle sends `_ptxn`; `ptxn` is accepted too in case a link is ever hand-built without it.
	const rawTxn = (searchParams.get('_ptxn') || searchParams.get('ptxn') || '').trim()
	const transactionId = TXN_PATTERN.test(rawTxn) ? rawTxn : ''

	const fail = (text: string) => {
		setMessage(text)
		setPhase('error')
	}

	useEffect(() => {
		if (!transactionId) {
			fail('No payment reference was supplied. Please open the invoice from your account and pay from there.')
			return
		}

		let cancelled = false

		BillingService.getPaddleCheckoutContext()
			.then((res) => {
				const context = res?.body
				if (!context?.configured) {
					throw new Error('Checkout is unavailable right now. Please contact support.')
				}
				return initPaddle(context, () => {
					// The webhook is what marks the invoice paid; this only moves the customer
					// somewhere sensible once Paddle confirms.
					setPhase('done')
					setTimeout(() => {
						window.location.href = INVOICE_LIST
					}, 2500)
				})
			})
			.then(() => {
				if (cancelled) return
				setReady(true)
				openPaddleCheckout(transactionId)
				// The overlay can be dismissed without any event firing, so offer a way back in.
				setTimeout(() => setPhase((p) => (p === 'opening' ? 'retry' : p)), 1500)
			})
			.catch((err: any) => {
				if (cancelled) return
				fail(err?.msg || err?.message || 'Could not open the checkout. Please contact support.')
			})

		return () => {
			cancelled = true
		}
		// eslint-disable-next-line
	}, [transactionId])

	const reopen = useCallback(() => {
		try {
			openPaddleCheckout(transactionId)
		} catch (err: any) {
			fail(err?.message || 'Could not open the checkout. Please contact support.')
		}
	}, [transactionId])

	// Standalone on purpose (see App.tsx): no layout, so it draws its own page, in the sign-in
	// screens' look.
	return (
		<div className='erp-auth'>
			<div className='card erp-auth__card erp-auth__card--narrow'>
				<div className='card-body erp-auth__body text-center'>
					<img src={ENV.brandLogo} alt={ENV.brandName} className='erp-auth__logo mb-4' />

					<h1 className='h4 mb-3'>Complete your payment</h1>

					{phase === 'opening' && (
						<>
							<div className='spinner-border text-primary' role='status' aria-hidden='true' />
							<p className='mt-3 mb-0 text-body-secondary'>Opening secure checkout&hellip;</p>
						</>
					)}

					{phase === 'retry' && (
						<>
							<p className='text-body-secondary'>The checkout window did not open, or you closed it.</p>
							<button type='button' className='btn btn-primary' onClick={reopen} disabled={!ready}>
								<Icon icon='lock' className='me-2' /> Open checkout
							</button>
						</>
					)}

					{phase === 'done' && (
						<>
							<Icon icon='check-circle-fill' color='success' size={48} />
							<p className='mt-3 mb-0 text-body-secondary'>Payment received. Redirecting&hellip;</p>
						</>
					)}

					{phase === 'error' && <div className='alert alert-danger mb-0 text-start'>{message}</div>}

					<a href={INVOICE_LIST} className='btn btn-link mt-4'>
						Go to my invoices
					</a>
				</div>
			</div>
		</div>
	)
}

export default PaddleCheckout
